/* Shopify path: assets/pro-scrolling-media-grid.js */
(function () {
  'use strict';

  var runtimeKey = 'ProScrollingMediaGridRuntime';
  var sectionSelector = 'section.pro-scroll-media[id^="ProScrollingMediaGrid-"]';
  var coreKey = '__proVideoLifecycleCoreV1';

  function initSection(section) {
    if (!section || !section.matches(sectionSelector)) return;
    if (typeof section.proScrollingMediaCleanup === 'function') return;

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var fallbackCleanup = [];
    var reducedMotionQuery = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
    var videoCore = window[coreKey] || null;

    function on(target, eventName, handler, options) {
      if (!target || typeof target.addEventListener !== 'function') return;

      if (controller) {
        var eventOptions = typeof options === 'boolean'
          ? { capture: options, signal: controller.signal }
          : Object.assign({}, options || {}, { signal: controller.signal });

        target.addEventListener(eventName, handler, eventOptions);
        return;
      }

      target.addEventListener(eventName, handler, options);
      fallbackCleanup.push(function () {
        target.removeEventListener(eventName, handler, options);
      });
    }

    var root = section.querySelector('[data-pro-media-carousel]');
    if (!root) return;

    var viewport = root.querySelector('[data-carousel-viewport]');
    var track = root.querySelector('[data-carousel-track]');
    var prevButton = root.querySelector('[data-carousel-prev]');
    var nextButton = root.querySelector('[data-carousel-next]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-carousel-dot]'));

    if (!viewport || !track) return;

    var slideByOrder = [];
    Array.prototype.slice.call(track.querySelectorAll('[data-carousel-slide]')).forEach(function (slide) {
      var order = parseInt(slide.getAttribute('data-slide-order'), 10);
      if (!isNaN(order)) slideByOrder[order] = slide;
    });

    slideByOrder = slideByOrder.filter(Boolean);
    var realCount = slideByOrder.length;
    if (!realCount) return;

    var currentOrder = 0;
    var currentTranslate = 0;
    var autoTimer = null;
    var fallbackTimer = null;
    var orientationTimer = null;
    var dragResetTimer = null;
    var queuedSteps = [];
    var preparedVideos = [];
    var metadataPreparedVideos = [];
    var autoEnabled = root.getAttribute('data-auto-enabled') === 'true';
    var autoInterval = parseInt(root.getAttribute('data-auto-interval'), 10) || 3000;
    var isDragging = false;
    var wasDragging = false;
    var isAnimating = false;
    var dragStartX = 0;
    var dragDeltaX = 0;
    var dragPointerId = null;
    var pendingDirection = null;
    var isSectionInView = false;
    var isSectionNearView = false;
    var intersectionObserver = null;
    var preloadObserver = null;
    var isCleanedUp = false;
    var preloadOwner = {};

    function orderedSlides() {
      return Array.prototype.slice.call(track.children).filter(function (child) {
        return child.hasAttribute('data-carousel-slide');
      });
    }

    function getActiveDomIndex() {
      return realCount > 1 ? 1 : 0;
    }

    function getActiveSlide() {
      return slideByOrder[currentOrder];
    }

    function getVideoSlides() {
      return slideByOrder;
    }

    function prepareVideo(video) {
      if (!video || preparedVideos.indexOf(video) > -1) return;

      preparedVideos.push(video);

      if (videoCore?.helpers?.prepareVideo) {
        videoCore.helpers.prepareVideo(video, {
          autoplayAttribute: false,
          preload: video.preload || 'none',
          loopMode: 'runtime'
        });
      } else {
        video.muted = true;
        video.defaultMuted = true;
        video.loop = false;
        video.autoplay = false;
        video.playsInline = true;
        video.controls = false;
        video.setAttribute('muted', '');
        video.removeAttribute('loop');
        video.setAttribute('playsinline', '');
        video.removeAttribute('autoplay');
        video.removeAttribute('controls');
      }

      on(video, 'play', function () {
        var slide = video.closest('[data-carousel-slide]');
        if (slide) slide.classList.add('is-playing');
        restartAuto();
      });

      on(video, 'pause', function () {
        var slide = video.closest('[data-carousel-slide]');
        if (slide) slide.classList.remove('is-playing');
        restartAuto();
      });

      on(video, 'ended', function () {
        var slide = video.closest('[data-carousel-slide]');
        if (
          !slide ||
          slide !== getActiveSlide() ||
          !isSectionInView ||
          document.hidden ||
          isCleanedUp
        ) {
          videoCore?.playback?.release(section);
          return;
        }

        try { video.currentTime = 0; } catch (error) {}
        playSlideVideo(slide, true);
      });

      ['waiting', 'stalled'].forEach(function (eventName) {
        on(video, eventName, function () {
          videoCore?.helpers?.reportStall?.(video);
        }, { passive: true });
      });
    }

    function bindAllVideos() {
      getVideoSlides().forEach(function (slide) {
        var video = slide.querySelector('video');
        if (video) prepareVideo(video);
      });
    }

    function prepareActiveVideoMetadata() {
      if (isCleanedUp) return;

      var slide = getActiveSlide();
      var video = slide ? slide.querySelector('video') : null;

      if (!isSectionNearView || !video) {
        videoCore?.preload?.unregister?.(preloadOwner);
        return;
      }

      prepareVideo(video);
      if (metadataPreparedVideos.indexOf(video) === -1) metadataPreparedVideos.push(video);

      /*
       * This section used to promote its active video directly to preload=auto,
       * outside the shared resource budget. Register the active slide with the
       * global single-owner queue instead, so Media + Content / UGC / Orbit and
       * this carousel cannot all start speculative downloads at once.
       */
      if (videoCore?.preload && videoCore?.helpers?.setPreload) {
        videoCore.preload.register(preloadOwner, {
          root: section,
          video: video,
          warmMode: 'adaptive',
          isUrgent: function () {
            return isSectionInView || isSectionNearView;
          },
          isEligible: function () {
            var policy = videoCore.helpers.resourcePolicy();
            return !isCleanedUp && isSectionNearView &&
              policy.speculativePreload !== 'none';
          },
          onGrant: function () {
            var policy = videoCore.helpers.resourcePolicy();
            var intent = policy.speculativePreload || 'none';
            videoCore.helpers.setPreload(video, intent, {
              load: intent !== 'none',
              recover: true
            });
          },
          onRevoke: function () {
            if (isCleanedUp || !video.paused) return;
            var policy = videoCore.helpers.resourcePolicy();
            var intent = policy.farPreload || 'metadata';
            videoCore.helpers.setPreload(video, intent, {
              load: intent !== 'none'
            });
          }
        });
        return;
      }

      // Core-unavailable fallback: warm metadata only; never fan out auto loads.
      try {
        video.preload = 'metadata';
        video.setAttribute('preload', 'metadata');
        if (video.readyState === 0 && typeof video.load === 'function') video.load();
      } catch (error) {}
    }

    function isAnyVideoPlaying() {
      return getVideoSlides().some(function (slide) {
        var video = slide.querySelector('video');
        return video && !video.paused && !video.ended;
      });
    }

    function pauseAllVideos(reset, releasePlayback) {
      getVideoSlides().forEach(function (slide) {
        var video = slide.querySelector('video');
        if (!video) return;

        try {
          video.pause();
          if (reset && video.readyState > 0) video.currentTime = 0;
        } catch (error) {}

        slide.classList.remove('is-playing');
      });

      if (releasePlayback !== false) videoCore?.playback?.release(section);
    }

    function setInitialOrder(order) {
      if (realCount <= 1) {
        track.appendChild(slideByOrder[0]);
        currentOrder = 0;
        return;
      }

      currentOrder = (order + realCount) % realCount;

      var orderList = [];
      for (var i = 0; i < realCount; i++) {
        orderList.push((currentOrder - 1 + i + realCount) % realCount);
      }

      orderList.forEach(function (slideOrder) {
        track.appendChild(slideByOrder[slideOrder]);
      });
    }

    function getTargetTranslateForSlide(slide) {
      if (!slide) return currentTranslate;
      var viewportWidth = viewport.clientWidth;
      var slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      return viewportWidth / 2 - slideCenter;
    }

    function getActiveDomSlide() {
      return orderedSlides()[getActiveDomIndex()];
    }

    function syncArrowCenter() {
      var activeSlide = getActiveDomSlide();
      var carousel = root.querySelector('.pro-scroll-media__carousel');
      if (!activeSlide || !carousel) return;

      var mediaWrap = activeSlide.querySelector('.pro-scroll-media__media-wrap');
      if (!mediaWrap) return;

      var carouselRect = carousel.getBoundingClientRect();
      var mediaRect = mediaWrap.getBoundingClientRect();
      if (!mediaRect.height) return;

      var arrowCenter = (mediaRect.top - carouselRect.top) + (mediaRect.height / 2);
      var arrowCenterPx = arrowCenter + 'px';

      section.style.setProperty('--psm-arrow-top-desktop', arrowCenterPx);
      section.style.setProperty('--psm-arrow-top-mobile', arrowCenterPx);
    }

    function setTrackTransition(enabled) {
      track.style.transition = enabled ? 'transform 0.45s cubic-bezier(0.22, 0.61, 0.36, 1)' : 'none';
    }

    function forceReflow() {
      return track.offsetHeight;
    }

    function updateClasses() {
      var children = orderedSlides();
      var activeIndex = -1;

      children.forEach(function (slide, index) {
        var order = parseInt(slide.getAttribute('data-slide-order'), 10);
        if (order === currentOrder) activeIndex = index;
      });

      children.forEach(function (slide, index) {
        var order = parseInt(slide.getAttribute('data-slide-order'), 10);
        var isActive = order === currentOrder;

        slide.classList.toggle('is-active', isActive);
        slide.classList.toggle('is-prev', activeIndex > -1 && index === activeIndex - 1 && realCount > 1);
        slide.classList.toggle('is-next', activeIndex > -1 && index === activeIndex + 1 && realCount > 2);
        slide.classList.toggle('is-buffer', activeIndex > -1 && index === activeIndex + 2 && realCount > 3);
      });

      dots.forEach(function (dot) {
        dot.classList.toggle('is-active', dot.getAttribute('data-slide-order') === String(currentOrder));
      });
    }

    function getCssNumber(name, fallback) {
      var value = window.getComputedStyle(section).getPropertyValue(name);
      var parsed = parseFloat(value);
      return isNaN(parsed) ? fallback : parsed;
    }

    function lerp(start, end, progress) {
      return start + ((end - start) * progress);
    }

    function getSideScale() {
      return {
        x: getCssNumber('--psm-current-side-scale-x', 0.72),
        y: getCssNumber('--psm-current-side-scale-y', 0.84),
        pull: getCssNumber('--psm-current-side-pull', 0)
      };
    }

    function getMediaButton(slide) {
      return slide ? slide.querySelector('[data-media-button]') : null;
    }

    function getPullForSlide(slide, side) {
      if (!slide || !side) return 0;
      if (slide.classList.contains('is-prev')) return side.pull;
      if (slide.classList.contains('is-next') || slide.classList.contains('is-buffer')) return -side.pull;
      return 0;
    }

    function setMediaScale(slide, scaleX, scaleY, opacity, side, pullOverride) {
      var mediaButton = getMediaButton(slide);
      if (!mediaButton) return;

      var pull = typeof pullOverride === 'number' ? pullOverride : getPullForSlide(slide, side || getSideScale());
      var transformValue = 'translateX(' + pull + 'px) scale(' + scaleX + ', ' + scaleY + ')';

      mediaButton.style.transform = transformValue;
      mediaButton.style.webkitTransform = transformValue;

      if (typeof opacity === 'number') {
        mediaButton.style.opacity = String(opacity);
      }
    }

    function clearDragMorph() {
      orderedSlides().forEach(function (slide) {
        slide.style.opacity = '';

        var mediaButton = getMediaButton(slide);
        if (!mediaButton) return;

        mediaButton.style.transform = '';
        mediaButton.style.webkitTransform = '';
        mediaButton.style.opacity = '';
      });
    }

    function applyDragMorph(deltaX) {
      var children = orderedSlides();
      if (!children.length) return;

      var maxDrag = Math.max(90, viewport.clientWidth * 0.32);
      var progress = Math.min(Math.abs(deltaX) / maxDrag, 1);
      var side = getSideScale();

      var prev = children[0];
      var active = children[1] || children[0];
      var next = children[2];
      var buffer = children[3];

      if (deltaX < 0) {
        if (prev) {
          setMediaScale(prev, side.x, side.y, Math.max(0.25, 1 - progress), side, side.pull);
        }

        if (active) {
          setMediaScale(
            active,
            lerp(1, side.x, progress),
            lerp(1, side.y, progress),
            1,
            side,
            lerp(0, side.pull, progress)
          );
        }

        if (next) {
          setMediaScale(
            next,
            lerp(side.x, 1, progress),
            lerp(side.y, 1, progress),
            1,
            side,
            lerp(-side.pull, 0, progress)
          );
        }

        if (buffer) {
          setMediaScale(buffer, side.x, side.y, Math.min(1, Math.max(0.35, progress * 1.25)), side, -side.pull);
        }

        return;
      }

      if (deltaX > 0) {
        if (prev) {
          setMediaScale(
            prev,
            lerp(side.x, 1, progress),
            lerp(side.y, 1, progress),
            1,
            side,
            lerp(side.pull, 0, progress)
          );
        }

        if (active) {
          setMediaScale(
            active,
            lerp(1, side.x, progress),
            lerp(1, side.y, progress),
            1,
            side,
            lerp(0, -side.pull, progress)
          );
        }

        if (next) {
          setMediaScale(next, side.x, side.y, Math.max(0.35, 1 - (progress * 0.2)), side, -side.pull);
        }

        return;
      }

      clearDragMorph();
    }

    function centerActive(animate) {
      clearDragMorph();

      var active = getActiveDomSlide();
      if (!active) return;

      currentTranslate = getTargetTranslateForSlide(active);
      setTrackTransition(animate !== false);
      track.style.transform = 'translate3d(' + currentTranslate + 'px, 0, 0)';
      updateClasses();
      syncArrowCenter();
      window.requestAnimationFrame(syncArrowCenter);

      if (animate === false) {
        forceReflow();
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            setTrackTransition(true);
          });
        });
      }
    }

    function finishMove() {
      window.clearTimeout(fallbackTimer);
      track.classList.remove('is-moving');

      if (!pendingDirection) {
        isAnimating = false;
        centerActive(false);
        runQueuedStep();
        return;
      }

      setTrackTransition(false);

      if (pendingDirection === 'next') {
        var first = orderedSlides()[0];
        if (first) track.appendChild(first);
      } else if (pendingDirection === 'prev') {
        var children = orderedSlides();
        var last = children[children.length - 1];
        if (last) track.insertBefore(last, children[0]);
      }

      pendingDirection = null;
      centerActive(false);
      isAnimating = false;
      restartAuto();
      runQueuedStep();
    }

    function animateToSlide(slide, direction, resetVideo) {
      if (!slide || isAnimating) return;
      if (resetVideo) pauseAllVideos(true);

      isAnimating = true;
      pendingDirection = direction;
      currentTranslate = getTargetTranslateForSlide(slide);
      track.classList.add('is-moving');
      prepareActiveVideoMetadata();

      setTrackTransition(true);
      updateClasses();
      track.style.transform = 'translate3d(' + currentTranslate + 'px, 0, 0)';

      window.requestAnimationFrame(function () {
        clearDragMorph();
      });

      window.clearTimeout(fallbackTimer);
      fallbackTimer = window.setTimeout(finishMove, 650);
      restartAuto();
    }

    function goNext(resetVideo) {
      if (realCount <= 1) return;
      if (isAnimating) {
        queuedSteps.push('next');
        return;
      }

      var children = orderedSlides();
      var target = children[2] || children[1];
      currentOrder = (currentOrder + 1) % realCount;
      animateToSlide(target, 'next', resetVideo !== false);
    }

    function goPrev(resetVideo) {
      if (realCount <= 1) return;
      if (isAnimating) {
        queuedSteps.push('prev');
        return;
      }

      var children = orderedSlides();
      var target = children[0] || children[1];
      currentOrder = (currentOrder - 1 + realCount) % realCount;
      animateToSlide(target, 'prev', resetVideo !== false);
    }

    function runQueuedStep() {
      if (!queuedSteps.length || isAnimating) return;
      var next = queuedSteps.shift();
      if (next === 'next') goNext(true);
      if (next === 'prev') goPrev(true);
    }

    function goToOrder(order) {
      order = parseInt(order, 10);
      if (isNaN(order) || order === currentOrder || realCount <= 1) return;

      queuedSteps = [];

      var forward = (order - currentOrder + realCount) % realCount;
      var backward = (currentOrder - order + realCount) % realCount;
      var direction = forward <= backward ? 'next' : 'prev';
      var steps = Math.min(forward, backward);

      for (var i = 0; i < steps - 1; i++) queuedSteps.push(direction);

      if (direction === 'next') goNext(true);
      else goPrev(true);
    }

    function playSlideVideo(slide, fromRuntimeLoop) {
      var video = slide.querySelector('video');
      if (!video) return;

      prepareVideo(video);
      pauseAllVideos(!fromRuntimeLoop, false);

      try {
        video.muted = true;
        video.defaultMuted = true;
        video.loop = false;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.removeAttribute('loop');
        video.setAttribute('playsinline', '');
        video.removeAttribute('controls');

        if (videoCore?.playback) {
          var claimed = videoCore.playback.claim(section, {
            priority: videoCore.playback.priorities.MANUAL,
            allowEqualPreempt: true,
            root: section,
            video: video,
            pause: function () { pauseAllVideos(false, false); }
          });
          if (!claimed) return;
        }

        /* Manual intent owns the resource budget before preload=auto. */
        if (videoCore?.helpers?.setPreload) {
          videoCore.helpers.setPreload(video, 'auto', { load: true });
        } else {
          video.preload = 'auto';
          video.setAttribute('preload', 'auto');
          if (video.readyState === 0 && typeof video.load === 'function') video.load();
        }

        var playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {
            videoCore?.playback?.release(section);
          });
        }
      } catch (error) {
        videoCore?.playback?.release(section);
      }
    }

    function stopAuto() {
      window.clearInterval(autoTimer);
      autoTimer = null;
    }

    function restartAuto() {
      stopAuto();

      if (
        isCleanedUp
        || !autoEnabled
        || !isSectionInView
        || document.hidden
        || realCount <= 1
        || (reducedMotionQuery && reducedMotionQuery.matches)
      ) return;

      autoTimer = window.setInterval(function () {
        if (document.hidden || !isSectionInView || isAnyVideoPlaying() || isDragging || isAnimating) return;
        goNext(true);
      }, autoInterval);
    }

    on(track, 'transitionend', function (event) {
      if (event.target !== track || event.propertyName !== 'transform') return;
      finishMove();
    });

    if (prevButton) on(prevButton, 'click', function () { goPrev(true); });
    if (nextButton) on(nextButton, 'click', function () { goNext(true); });

    dots.forEach(function (dot) {
      on(dot, 'click', function () { goToOrder(dot.getAttribute('data-slide-order')); });
    });

    root.querySelectorAll('[data-media-button]').forEach(function (mediaButton) {
      on(mediaButton, 'click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (wasDragging) return;

        var slide = mediaButton.closest('[data-carousel-slide]');
        if (!slide) return;

        var order = parseInt(slide.getAttribute('data-slide-order'), 10);
        if (order !== currentOrder) {
          goToOrder(order);
          return;
        }

        var video = slide.querySelector('video');
        if (!video) return;

        if (!video.paused) pauseAllVideos(true);
        else playSlideVideo(slide);
      });
    });

    on(viewport, 'pointerdown', function (event) {
      if (event.button && event.button !== 0) return;
      if (event.pointerType === 'mouse' && event.target.closest('[data-media-button]')) return;
      if (isAnimating) return;

      isDragging = true;
      wasDragging = false;
      dragStartX = event.clientX;
      dragDeltaX = 0;
      dragPointerId = event.pointerId;
      viewport.classList.add('is-dragging');

      try { viewport.setPointerCapture(dragPointerId); } catch (error) {}
      clearDragMorph();
      track.classList.add('is-moving');
      setTrackTransition(false);
    });

    on(viewport, 'pointermove', function (event) {
      if (!isDragging) return;
      dragDeltaX = event.clientX - dragStartX;
      if (Math.abs(dragDeltaX) > 6) wasDragging = true;
      track.style.transform = 'translate3d(' + (currentTranslate + dragDeltaX) + 'px, 0, 0)';
      applyDragMorph(dragDeltaX);
    });

    function endDrag() {
      if (!isDragging) return;

      isDragging = false;
      viewport.classList.remove('is-dragging');
      try { viewport.releasePointerCapture(dragPointerId); } catch (error) {}

      if (Math.abs(dragDeltaX) <= 48) {
        track.classList.remove('is-moving');
        clearDragMorph();
        centerActive(true);
      } else {
        if (dragDeltaX < 0) goNext(true);
        else goPrev(true);
      }

      window.clearTimeout(dragResetTimer);
      dragResetTimer = window.setTimeout(function () {
        wasDragging = false;
      }, 100);
    }

    on(viewport, 'pointerup', endDrag);
    on(viewport, 'pointercancel', endDrag);
    on(viewport, 'pointerleave', function (event) {
      if (event.pointerType === 'mouse') endDrag();
    });

    function handleSectionEnter() {
      if (isCleanedUp) return;
      isSectionInView = true;
      restartAuto();
    }

    function handleSectionLeave() {
      if (!isSectionInView) return;
      isSectionInView = false;
      stopAuto();
      pauseAllVideos(false);
    }

    function setupPreloadObserver() {
      if (preloadObserver) {
        preloadObserver.disconnect();
        preloadObserver = null;
      }

      if (!('IntersectionObserver' in window)) return;

      preloadObserver = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        if (!entry || isCleanedUp) return;

        isSectionNearView = entry.isIntersecting;
        prepareActiveVideoMetadata();
      }, {
        threshold: 0,
        rootMargin: '1400px 0px 1400px 0px'
      });

      preloadObserver.observe(section);
    }

    function setupVisibilityObserver() {
      if (intersectionObserver) {
        intersectionObserver.disconnect();
        intersectionObserver = null;
      }

      if (!('IntersectionObserver' in window)) {
        isSectionInView = true;
        restartAuto();
        return;
      }

      intersectionObserver = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        if (!entry || isCleanedUp) return;

        if (entry.isIntersecting && entry.intersectionRatio >= 0.15) {
          handleSectionEnter();
        } else {
          handleSectionLeave();
        }
      }, {
        threshold: [0, 0.15, 0.35, 0.6],
        rootMargin: '0px 0px -8% 0px'
      });

      intersectionObserver.observe(section);
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        stopAuto();
        pauseAllVideos(false);
        return;
      }

      if (isSectionInView) restartAuto();
    }

    function handlePageHide(event) {
      pauseAllVideos(false);
      stopAuto();

      if (!event.persisted) cleanup();
    }

    function handlePageShow(event) {
      if (isCleanedUp || !event.persisted) return;
      centerActive(false);
      if (isSectionInView && !document.hidden) restartAuto();
    }

    on(document, 'visibilitychange', handleVisibilityChange);

    on(window, 'resize', function () { centerActive(false); });
    on(window, 'orientationchange', function () {
      window.clearTimeout(orientationTimer);
      orientationTimer = window.setTimeout(function () {
        centerActive(false);
      }, 250);
    });

    on(document, 'shopify:section:load', function (event) {
      if (event.target && event.target.contains(section)) {
        setInitialOrder(currentOrder);
        centerActive(false);
        restartAuto();
      }
    });

    if (
      reducedMotionQuery
      && typeof reducedMotionQuery.addEventListener === 'function'
    ) {
      on(reducedMotionQuery, 'change', restartAuto);
    }

    function cleanup() {
      if (isCleanedUp) return;
      isCleanedUp = true;

      stopAuto();
      window.clearTimeout(fallbackTimer);
      window.clearTimeout(orientationTimer);
      window.clearTimeout(dragResetTimer);
      fallbackTimer = null;
      orientationTimer = null;
      dragResetTimer = null;
      queuedSteps = [];

      if (intersectionObserver) {
        intersectionObserver.disconnect();
        intersectionObserver = null;
      }

      if (preloadObserver) {
        preloadObserver.disconnect();
        preloadObserver = null;
      }
      isSectionNearView = false;
      videoCore?.preload?.unregister?.(preloadOwner);

      pauseAllVideos(false);

      if (controller) {
        controller.abort();
      } else {
        fallbackCleanup.splice(0).forEach(function (removeListener) {
          removeListener();
        });
      }

      if (section.proScrollingMediaCleanup === cleanup) {
        delete section.proScrollingMediaCleanup;
      }
    }

    section.proScrollingMediaCleanup = cleanup;

    on(document, 'shopify:section:unload', function (event) {
      if (
        event.target === section
        || (event.target && event.target.contains(section))
      ) {
        cleanup();
      }
    });

    on(window, 'pagehide', handlePageHide);
    on(window, 'pageshow', handlePageShow);

    setInitialOrder(0);
    bindAllVideos();
    centerActive(false);
    setupPreloadObserver();
    setupVisibilityObserver();
  }

  function initWithin(root) {
    if (!root) return;

    if (root.nodeType === 1 && root.matches(sectionSelector)) {
      initSection(root);
    }

    if (typeof root.querySelectorAll === 'function') {
      Array.prototype.forEach.call(root.querySelectorAll(sectionSelector), initSection);
    }
  }

  var existingRuntime = window[runtimeKey];
  if (existingRuntime && typeof existingRuntime.initWithin === 'function') {
    existingRuntime.initWithin(document);
    return;
  }

  window[runtimeKey] = {
    initSection: initSection,
    initWithin: initWithin,
  };

  document.addEventListener('shopify:section:load', function (event) {
    initWithin(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initWithin(document);
    }, { once: true });
  } else {
    initWithin(document);
  }
})();
